/**
 * First-touch acquisition source for leads from the marketing site. A
 * first-party, strictly-necessary cookie: it never leaves our servers except
 * inside the lead itself, so it is written before consent. Ad-platform click
 * cookies (_fbc, _fbp) are separate and only exist after advertising consent.
 */
export const ATTRIBUTION_COOKIE = 'af_attr';

const ATTRIBUTION_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const MAX_VALUE_LENGTH = 150;
/** Browsers silently drop cookies over ~4KB; stay well under it. */
const MAX_COOKIE_VALUE_LENGTH = 3000;

const TRACKED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'li_fat_id',
] as const;

type TrackedParam = (typeof TRACKED_PARAMS)[number];

export type MarketingAttribution = Partial<Record<TrackedParam, string>> & {
  /** Referrer origin + path only; query strings are dropped. */
  referrer?: string;
  landingPath: string;
  /** First-touch time, epoch milliseconds. */
  ts: number;
};

function clip(value: string): string {
  return value.slice(0, MAX_VALUE_LENGTH);
}

function referrerWithoutQuery(referrer: string, ownHost: string): string | undefined {
  try {
    const url = new URL(referrer);

    if (url.host === ownHost) return undefined;

    return clip(`${url.origin}${url.pathname}`);
  } catch {
    return undefined;
  }
}

export function parseAttribution(
  pageUrl: string,
  referrer: string,
  now: number,
): MarketingAttribution {
  const url = new URL(pageUrl);
  const attribution: MarketingAttribution = {
    landingPath: clip(url.pathname),
    ts: now,
  };

  for (const param of TRACKED_PARAMS) {
    const value = url.searchParams.get(param);
    if (value) attribution[param] = clip(value);
  }

  const source = referrer ? referrerWithoutQuery(referrer, url.host) : undefined;
  if (source) attribution.referrer = source;

  return attribution;
}

export function serializeAttribution(attribution: MarketingAttribution): string {
  return encodeURIComponent(JSON.stringify(attribution));
}

export function readAttribution(value: string | null | undefined): MarketingAttribution | null {
  if (!value) return null;

  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(value));

    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as MarketingAttribution).landingPath !== 'string' ||
      typeof (parsed as MarketingAttribution).ts !== 'number'
    ) {
      return null;
    }

    const source = parsed as Record<string, unknown>;
    const attribution: MarketingAttribution = {
      landingPath: clip(String(source.landingPath)),
      ts: Number(source.ts),
    };

    for (const key of [...TRACKED_PARAMS, 'referrer'] as const) {
      const value = source[key];
      if (typeof value === 'string' && value) attribution[key] = clip(value);
    }

    return attribution;
  } catch {
    return null;
  }
}

export function attributionCookie(attribution: MarketingAttribution): string {
  let value = serializeAttribution(attribution);

  // Keep the fields that identify the campaign; drop the rest if oversized.
  if (value.length > MAX_COOKIE_VALUE_LENGTH) {
    const { landingPath, ts, utm_source, utm_medium, utm_campaign, gclid, fbclid } = attribution;
    value = serializeAttribution({ landingPath, ts, utm_source, utm_medium, utm_campaign, gclid, fbclid });
  }

  return `${ATTRIBUTION_COOKIE}=${value}; Path=/; Max-Age=${ATTRIBUTION_MAX_AGE_SECONDS}; SameSite=Lax; Secure`;
}

/** Meta's click-id cookie format: fb.<subdomain index>.<creation ms>.<fbclid>. */
export function buildFbc(fbclid: string, createdAt: number): string {
  return `fb.1.${createdAt}.${fbclid}`;
}
